import { z } from 'zod';
import * as Y from 'yjs';
import { DEFAULT_BACKGROUND, DEFAULT_GRID, isBoardMetaPatch, readMeta, type BoardMeta } from './meta';
import { stableJson } from './restore';
import { plainToRich, richToPlain, type RichDoc } from './rich';
import { parseObject, SCHEMA_VERSION, type ObjectData } from './schema';
import { createBoardDoc } from './store';
import { withAuthor, type Authorship } from './commands';
import { isTextType, readObject, writeObject } from './ytypes';
import { generateNKeysBetween } from 'fractional-indexing';
import { PAGE_LIMIT, PAGE_MAIN, PAGE_NAME_MAX, pageAssignment, readPages, type PageEntry } from './pages';

export const BOARD_FILE_LIMITS = { objects: 20_000, assets: 5_000 } as const;

/** An asset the file refers to: `url` for a JSON export, `file` (path in the archive) for an `.osboard`. */
export type BoardFileAsset = { id: string; sha256: string | null; mime: string; w: number; h: number; url: string | null; file: string | null };

/** A page of a `BoardFile`, in display order (P14). */
export type BoardFilePage = { id: string; name: string };

/**
 * `BoardFile` (spec §12): the validated JSON view of a board, ids and z order kept (plan P9 Ruling 8). Version 2 (P14
 * Ruling 8) lists the pages, and each object carries the page it is shown on; a version 1 file is one page.
 */
export type BoardFile = {
  format: 'os-board';
  version: 1 | 2;
  schemaVersion: number;
  exportedAt: string;
  title: string;
  meta: BoardMeta;
  pages: BoardFilePage[];
  objects: ObjectData[];
  assets: BoardFileAsset[];
};

export class BoardFileError extends Error {
  constructor(readonly code: 'invalid' | 'version' | 'too_large') {
    super(code);
    this.name = 'BoardFileError';
  }
}

const assetSchema = z.object({
  id: z.string().min(1).max(64),
  sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  mime: z.string().max(100),
  w: z.number().finite().min(0),
  h: z.number().finite().min(0),
  url: z.string().max(2048).nullable(),
  file: z.string().max(200).nullable(),
});
const envelope = z.object({
  format: z.literal('os-board'),
  version: z.number(),
  schemaVersion: z.number().int().min(1),
  exportedAt: z.string().max(64),
  title: z.string().max(200),
  meta: z.object({ background: z.unknown(), grid: z.unknown() }).partial().passthrough(),
  pages: z.array(z.unknown()).optional(),
  objects: z.array(z.unknown()),
  assets: z.array(z.unknown()),
});
const pageSchema = z.object({ id: z.string().min(1).max(64), name: z.string().transform((n) => n.slice(0, PAGE_NAME_MAX)) });

/** Each object's `pageId` set to the page it is shown on (stale or unknown fields resolved), so a file is self-consistent. */
function onTheirPages(objects: ObjectData[], pages: readonly BoardFilePage[]): ObjectData[] {
  const entries: PageEntry[] = (pages.length ? pages : [{ id: PAGE_MAIN, name: '' }]).map((p, i) => ({ ...p, order: String(i).padStart(4, '0') }));
  const where = pageAssignment(new Map(objects.map((o) => [o.id, o])), entries);
  return objects.map((o) => (o.pageId === where.get(o.id) ? o : { ...o, pageId: where.get(o.id)! }));
}

const byZ = (a: ObjectData, b: ObjectData) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Text types always carry their rich content (a pre-v2 object gets it from its plain text), and `text` follows it. */
function normalized(o: ObjectData): ObjectData {
  if (!isTextType(o.type)) return o;
  const t = o as Extract<ObjectData, { type: 'text' | 'sticky' | 'shape' }>;
  const content: RichDoc = t.content ?? plainToRich(t.text ?? '');
  return { ...t, content, text: richToPlain(content) } as ObjectData;
}

/** Image assets and link/embed thumbnails, each once. */
export function boardAssetIds(objects: readonly ObjectData[]): string[] {
  const ids = new Set<string>();
  for (const o of objects) {
    if (o.type === 'image' && o.assetId) ids.add(o.assetId);
    if ((o.type === 'link' || o.type === 'embed') && o.thumbAssetId) ids.add(o.thumbAssetId);
  }
  return [...ids];
}

/** Valid objects of the document in z order; an object the schema refuses is left out (it is not displayed either). */
export function toBoardFile(doc: Y.Doc, info: { title: string; exportedAt: string; assets: BoardFileAsset[] }): BoardFile {
  const objects: ObjectData[] = [];
  for (const m of doc.getMap<Y.Map<unknown>>('objects').values()) {
    const o = m instanceof Y.Map ? parseObject(readObject(m)) : null;
    if (o) objects.push(normalized(o));
  }
  const pages = readPages(doc).map(({ id, name }) => ({ id, name }));
  return {
    format: 'os-board',
    version: 2,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: info.exportedAt,
    title: info.title,
    meta: readMeta(doc.getMap('meta')),
    pages,
    objects: onTheirPages(objects.sort(byZ), pages),
    assets: info.assets,
  };
}

/** Never trusts its input: an invalid envelope throws, an invalid or duplicate object is skipped and counted. */
export function parseBoardFile(raw: unknown): { file: BoardFile; skipped: number } {
  const env = envelope.safeParse(raw);
  if (!env.success) throw new BoardFileError('invalid');
  const e = env.data;
  if ((e.version !== 1 && e.version !== 2) || e.schemaVersion > SCHEMA_VERSION) throw new BoardFileError('version');
  if (e.objects.length > BOARD_FILE_LIMITS.objects || e.assets.length > BOARD_FILE_LIMITS.assets) throw new BoardFileError('too_large');
  const pages: BoardFilePage[] = [];
  for (const r of e.version === 2 ? (e.pages ?? []) : []) {
    const p = pageSchema.safeParse(r);
    if (p.success && !pages.some((x) => x.id === p.data.id)) pages.push(p.data);
  }
  if (pages.length > PAGE_LIMIT) throw new BoardFileError('too_large');
  if (pages.length === 0) pages.push({ id: PAGE_MAIN, name: '' });
  const seen = new Set<string>();
  const objects: ObjectData[] = [];
  let skipped = 0;
  for (const r of e.objects) {
    const o = parseObject(r);
    if (!o || seen.has(o.id)) skipped++;
    else {
      seen.add(o.id);
      objects.push(normalized(o));
    }
  }
  const assets: BoardFileAsset[] = [];
  const assetIds = new Set<string>();
  for (const r of e.assets) {
    const a = assetSchema.safeParse(r);
    if (a.success && !assetIds.has(a.data.id)) {
      assetIds.add(a.data.id);
      assets.push(a.data);
    }
  }
  const meta: BoardMeta = {
    background: isBoardMetaPatch({ background: e.meta.background as BoardMeta['background'] }) && e.meta.background ? (e.meta.background as BoardMeta['background']) : DEFAULT_BACKGROUND,
    grid: isBoardMetaPatch({ grid: e.meta.grid as BoardMeta['grid'] }) && e.meta.grid ? (e.meta.grid as BoardMeta['grid']) : DEFAULT_GRID,
  };
  return {
    file: { format: 'os-board', version: 2, schemaVersion: e.schemaVersion, exportedAt: e.exportedAt, title: e.title, meta, pages, objects: onTheirPages(objects.sort(byZ), pages), assets },
    skipped,
  };
}

/** New objects whose image `assetId` and link/embed `thumbAssetId` follow `map`; ids absent from it are kept. */
export function remapAssets(objects: readonly ObjectData[], map: ReadonlyMap<string, string>): ObjectData[] {
  return objects.map((o) => {
    if (o.type === 'image' && map.has(o.assetId)) return { ...o, assetId: map.get(o.assetId)! };
    if ((o.type === 'link' || o.type === 'embed') && o.thumbAssetId && map.has(o.thumbAssetId)) return { ...o, thumbAssetId: map.get(o.thumbAssetId)! };
    return o;
  });
}

/** A fresh document holding the file: same ids and z, assets through `map`, board settings, in one transaction. */
/** `stamp`: the importer, recorded on every object (P5d); absent, the file's own authors stay (restore, copies). */
export function boardFileToDoc(file: BoardFile, map: ReadonlyMap<string, string> = new Map(), stamp?: Authorship): Y.Doc {
  const doc = createBoardDoc();
  doc.transact(() => {
    const meta = doc.getMap('meta');
    meta.set('background', { ...file.meta.background });
    meta.set('grid', { ...file.meta.grid });
    // One unnamed page stays implicit, as on any board written before pages (Ruling 4).
    const pages = file.pages ?? [];
    if (pages.length > 1 || pages.some((p) => p.name !== '')) {
      const keys = generateNKeysBetween(null, null, pages.length);
      const map = doc.getMap<Y.Map<unknown>>('pages');
      pages.forEach((p, i) => {
        const m = new Y.Map<unknown>();
        map.set(p.id, m);
        m.set('name', p.name);
        m.set('order', keys[i]!);
      });
    }
    const objects = doc.getMap<Y.Map<unknown>>('objects');
    for (const o of remapAssets(file.objects, map)) {
      const m = new Y.Map<unknown>();
      objects.set(o.id, m);
      writeObject(m, structuredClone(stamp ? withAuthor(o, stamp) : o) as Record<string, unknown>);
    }
  });
  return doc;
}

/** Ids present on one side only or whose objects differ (sorted-key JSON): empty means the same board. */
export function diffBoardObjects(a: readonly ObjectData[], b: readonly ObjectData[]): string[] {
  const left = new Map(a.map((o) => [o.id, stableJson(o)]));
  const right = new Map(b.map((o) => [o.id, stableJson(o)]));
  const out: string[] = [];
  for (const [id, json] of left) if (right.get(id) !== json) out.push(id);
  for (const id of right.keys()) if (!left.has(id)) out.push(id);
  return out;
}
